import { WsCsatRequestMessage } from '@dixa/messenger-api-payloads';

import type { ContactPointKind, ConversationsChannels } from './global';
import { Channel } from 'lib-shared-types-channels';

export namespace Satisfaction {
  export type RatingStyle = 'emojis' | 'stars' | 'numbers';
  export const ratingStyles: Satisfaction.RatingStyle[] = [
    'emojis',
    'numbers',
    'stars',
  ];

  export type CSATSettingsLevel =
    | 'channel'
    | 'contact_point'
    | 'language'
    | 'survey';
  export type CSATChannelsLevel =
    | 'email'
    | 'widgetchat'
    | 'messenger'
    | 'pstnphone';
  type EmailAddress = string;
  type WidgetId = string;
  type ContactPoint = EmailAddress | WidgetId;

  export const surveyStyles: SurveyType[] = ['text_to_speech', 'link'];

  export type SurveyType = 'text_to_speech' | 'link';

  export type ChannelSurveyType = {
    channel: Channel;
    supported_surveys: SurveyType[];
  };

  export const channelSurveyTypes: ChannelSurveyType[] = [
    { channel: 'pstn_phone', supported_surveys: ['link', 'text_to_speech'] },
  ];

  export type Response<TData> = {
    channel?: CSATChannelsLevel;
    data: TData;
    meta: {
      next_page: null;
      prev_page: null;
    };
  };

  export function prettySurveyType(surveyType: SurveyType): string {
    switch (surveyType) {
      case 'text_to_speech':
        return 'IVR';
      case 'link':
        return 'SMS';
      default:
        return surveyType;
    }
  }

  export interface SmsAdditionalSettings {
    sender_name?: string;
  }

  // additional settings of the link, for now only used for connecting phone numbers to link surveys
  export type LinkAdditionalSettings = SmsAdditionalSettings;

  export type RatingSettingsLink<
    TAdditionalSettings extends Partial<LinkAdditionalSettings> = Partial<LinkAdditionalSettings>,
  > = {
    rating_settings_id: string;
    contact_point: string;
    contact_point_kind: ContactPointKind;
    additional_settings?: TAdditionalSettings;
    name?: string;
    survey_type?: SurveyType;
    organization_id?: string;
  };

  export type CsatSettings<
    TCustomization extends Partial<CSATCustomization> = Partial<CSATCustomization>,
  > = {
    id: string;
    organization_id: string;
    channel?: CSATChannelsLevel;
    survey_type?: SurveyType;
    level: CSATSettingsLevel;
    enabled: boolean;
    customization: TCustomization;
    language?: string;
    created_at: string;
    updated_at: string;
    parent_id?: string;
    name?: string;
  };

  export interface EnrichedCsatSettings<
    TCustomization extends Partial<CSATCustomization> = Partial<CSATCustomization>,
  > extends CsatSettings<TCustomization> {
    // IMPORTANT: this field does not exist in the api response but it is added
    // via combining two api calls into one(/ratings/settings/{id}/link + /ratings/settings?level=email).
    contactPoint: string;
  }

  export type CsatDefaultSettings = {
    channel: ConversationsChannels;
    customization: EmailRatingCustomization;
  };

  export type CsatSettingsEnrichedGetResponse<
    TCustomization extends Partial<CSATCustomization> = Partial<CSATCustomization>,
  > = Response<{
    rating_settings: EnrichedCsatSettings<TCustomization>[];
    default_rating_settings?: CsatDefaultSettings;
  }>;

  export type CsatSettingsGetResponse<
    TCustomization extends Partial<ChannelCSATCustomization> = Partial<ChannelCSATCustomization>,
  > = Response<{
    rating_settings: CsatSettings<TCustomization>[];
    default_rating_settings?: CsatDefaultSettings;
  }>;

  export type EmailCsatSettingsGetResponse = CsatSettingsGetResponse<
    Partial<EmailRatingCustomization>
  >;
  export type ChatCsatSettingsGetResponse = CsatSettingsGetResponse<
    Partial<ChatRatingCustomization>
  >;
  export type DixaMessengerCsatSettingsGetResponse = CsatSettingsGetResponse<
    Partial<DixaMessengerRatingCustomization>
  >;
  export type EmailCsatSettingsEnrichedGetResponse =
    CsatSettingsEnrichedGetResponse<Partial<EmailRatingCustomization>>;
  export type ChatCsatSettingsEnrichedGetResponse =
    CsatSettingsEnrichedGetResponse<Partial<ChatRatingCustomization>>;
  export type DixaMessengerCsatSettingsEnrichedGetResponse =
    CsatSettingsEnrichedGetResponse<Partial<DixaMessengerRatingCustomization>>;

  export type CreateTextToSpeechCsatSettingsParams = Pick<
    TextToSpeechCsatSettings,
    | 'survey_type'
    | 'level'
    | 'customization'
    | 'name'
    | 'parent_id'
    | 'language'
  >;

  export type CreateLinkCsatSettingsParams = Pick<
    TextToSpeechCsatSettings,
    | 'survey_type'
    | 'level'
    | 'customization'
    | 'name'
    | 'parent_id'
    | 'language'
  >;

  export type CreateTextToSpeechSurveyCsatSettingsParams = Pick<
    TextToSpeechCsatSettings,
    'survey_type' | 'level' | 'name'
  >;

  export type CreateLinkSurveyCsatSettingsParams = Pick<
    LinkCsatSettings,
    'survey_type' | 'level' | 'name' | 'customization'
  >;

  export type CreateEmailCsatSettingsParams = Pick<
    EmailCsatSettings,
    'channel' | 'level' | 'customization'
  >;
  export type CreateChatCsatSettingsParams = Pick<
    ChatCsatSettings,
    'channel' | 'level' | 'customization'
  >;
  export type CreateDixaMessengerCsatSettingsParams = Pick<
    DixaMessengerCsatSettings,
    'channel' | 'level' | 'customization'
  >;

  // when updating csat settings settings a value to null has a specific meaning and should be allowed
  export type UpdateTextToSpeechCsatSettingsParams = Partial<
    Pick<TextToSpeechCsatSettings, 'id' | 'customization' | 'name'>
  >;

  // when updating csat settings settings a value to null has a specific meaning and should be allowed
  export type UpdateSmsSettingsParams = Partial<
    Pick<LinkCsatSettings, 'id' | 'customization' | 'name'>
  >;

  export type UpdateEmailCsatSettingsParams = CsatSettings<
    Partial<EmailRatingCustomization>
  >;
  export type UpdateChatCsatSettingsParams = CsatSettings<
    Partial<ChatRatingCustomization>
  >;
  export type UpdateDixaMessengerCsatSettingsParams = CsatSettings<
    Partial<DixaMessengerRatingCustomization>
  >;

  export type EmailCsatSettingsPostResponse = Response<
    CsatSettings<Partial<EmailRatingCustomization>>
  >;
  export type ChatCsatSettingsPostResponse = Response<
    CsatSettings<Partial<ChatRatingCustomization>>
  >;
  export type DixaMessengerCsatSettingsPostResponse = Response<
    CsatSettings<Partial<DixaMessengerRatingCustomization>>
  >;

  export type EmailCsatSettings = CsatSettings<
    Partial<EmailRatingCustomization>
  >;
  export type EmailCsatEnrichedSettings = EnrichedCsatSettings<
    Partial<EmailRatingCustomization>
  >;

  export type ChatCsatSettings = CsatSettings<Partial<ChatRatingCustomization>>;
  export type ChatCsatEnrichedSettings = EnrichedCsatSettings<
    Partial<ChatRatingCustomization>
  >;

  export type DixaMessengerCsatSettings = CsatSettings<
    Partial<DixaMessengerRatingCustomization>
  >;
  export type DixaMessengerCsatEnrichedSettings = EnrichedCsatSettings<
    Partial<DixaMessengerRatingCustomization>
  >;

  export type ContactPointLinkedToSettingResponse = Response<{
    contact_points: string[];
  }>;

  export interface GetSettingsParams {
    level?: CSATSettingsLevel;
    channel?: ConversationsChannels;
  }

  // To Update an existing setting
  export interface UpdateRatingSettingsInput {
    enabled?: boolean;
    overrideId?: string;
    customization?: CSATCustomization;
  }

  export interface LinkConversationInput {
    link_type?: 'email_forward';
  }

  /**
   * To assign a setting to a contact point A contact point is the id of a
   * conversation medium (email, messaging, ...)
   */
  export interface UpdateRatingSettingsLinksInput {
    // An array of all the contact points that are to be linked to the rating settings
    contact_points?: ContactPoint[];
  }

  export type CSATCustomization =
    | ChatRatingCustomization
    | EmailRatingCustomization
    | DixaMessengerRatingCustomization
    | TextToSpeechRatingCustomization
    | LinkRatingCustomization;

  export type ChannelCSATCustomization =
    | ChatRatingCustomization
    | EmailRatingCustomization
    | DixaMessengerRatingCustomization;

  export type TextToSpeechCsatSettings = CsatSettings<
    Partial<TextToSpeechRatingCustomization>
  >;

  export type TextToSpeechRatingCustomization = {
    introductory_message?: string;
    rating_questions?: string[];
    ending_message?: string;
  };

  export type LinkCsatSettings = CsatSettings<Partial<LinkRatingCustomization>>;

  export type LinkRatingCustomization = {
    look_and_feel?: Satisfaction.RatingStyle;
    delay?: number;
    sender_name?: string;
    rating_question?: string;
    thank_you_message?: string;
    comment_question?: string;
    placeholder_text?: string;
    button_text?: string;
    body?: string;
  };

  export interface EmailRatingCustomization {
    look_and_feel?: Satisfaction.RatingStyle;
    sender_email?: string | null;
    reply_to_email?: string | null;
    delay?: number;
    subject?: string;
    body?: string;
    rating_question?: string;
    thank_you_message?: string;
    comment_question?: string;
    placeholder_text?: string;
    button_text?: string;
  }

  export interface ChatRatingCustomization {
    look_and_feel?: Satisfaction.RatingStyle;
    rating_completed_message?: string;
    rating_follow_up_question?: string;
    rating_placeholder_text?: string;
    rating_question?: string;
  }

  export type DixaMessengerRatingCustomization = {
    rating_question: string;
    look_and_feel: WsCsatRequestMessage['content']['rating_type'];
    rating_placeholder_text: string;
    rating_follow_up_question: string;
    rating_completed_message: string;
  };

  export interface InitializeRatingSettingsInput {
    data: {
      channel: ConversationsChannels;
    };
  }
}
